import { type FC, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PageTitle } from "../../../components/PageTitle";
import { Button } from "../../../components/ui/button";
import {
  LICENSE_KEY,
  LICENSE_LABELS,
  type LicenseKey,
} from "../../../constants/licenses";
import {
  type UpdateTenantInput,
  useAdminChangeMemberRole,
  useAdminContractsApi,
  useAdminTenantApi,
  useCancelAdminContractApi,
  useDeleteAdminTenantApi,
  useToggleTenantLicenseApi,
  useUpdateAdminTenantApi,
} from "../../../hooks/useAdminApi";
import { toastPromise } from "../../../utils/toast-utils";

export const TenantDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: tenant,
    loading,
    error,
    refetch,
  } = useAdminTenantApi({
    id: id || "",
    skip: !id,
  });
  const { updateTenant } = useUpdateAdminTenantApi();
  const { deleteTenant } = useDeleteAdminTenantApi();
  const { enableLicense, disableLicense } = useToggleTenantLicenseApi();
  const { changeMemberRole, loading: isRoleChanging } =
    useAdminChangeMemberRole();
  const {
    data: contractsData,
    loading: contractsLoading,
    refetch: refetchContracts,
  } = useAdminContractsApi({ tenantId: id || "", skip: !id });
  const { cancelContract, loading: isCancelling } = useCancelAdminContractApi();

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<UpdateTenantInput>({});
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [contractCancelConfirm, setContractCancelConfirm] = useState<
    string | null
  >(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [roleChangeConfirm, setRoleChangeConfirm] = useState<{
    userId: string;
    name: string;
    newRole: string;
  } | null>(null);

  const handleEdit = () => {
    setEditForm({
      name: tenant?.name,
      description: tenant?.description || undefined,
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await updateTenant(id, editForm);
      await refetch();
      setIsEditing(false);
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleActive = async () => {
    if (!id || !tenant) return;
    setActionLoading(true);
    try {
      await updateTenant(id, { isActive: !tenant.isActive });
      await refetch();
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await deleteTenant(id);
      navigate("/admin/tenants");
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleLicense = async (key: LicenseKey, enabled: boolean) => {
    if (!id) return;
    const label = LICENSE_LABELS[key];
    const state = enabled ? "ON" : "OFF";
    setActionLoading(true);
    try {
      const action = enabled ? enableLicense(id, key) : disableLicense(id, key);
      await toastPromise(action, {
        success: `${label}のライセンスが${state}になりました。`,
        error: `${label}のライセンス変更に失敗しました。`,
      });
      await refetch();
    } catch (_e) {
      // toastPromise が既にエラー表示済み
    } finally {
      setActionLoading(false);
    }
  };

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>テナント詳細</PageTitle>
        <div className="alert alert-error">
          <svg
            className="stroke-current shrink-0 h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>エラーが発生しました: {error.message}</span>
        </div>
      </div>
    );
  }

  if (loading || !tenant) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>テナント詳細</PageTitle>
        <div className="flex justify-center py-8">
          <span className="loading loading-spinner loading-lg"></span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <PageTitle>テナント詳細</PageTitle>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/tenants")}
        >
          ← テナント一覧に戻る
        </Button>
      </div>

      {/* テナント情報 */}
      <div className="card bg-base-100 shadow-md mb-6">
        <div className="card-body">
          <div className="flex justify-between items-start mb-4">
            <h2 className="card-title text-color-title">テナント情報</h2>
            <div className="flex gap-2">
              <Link to={`/admin/usage`} className="btn btn-outline btn-sm">
                利用状況
              </Link>
              {!isEditing && (
                <>
                  <Button onClick={handleEdit} size="sm">
                    編集
                  </Button>
                  <Button
                    onClick={handleToggleActive}
                    disabled={actionLoading}
                    size="sm"
                    className={tenant.isActive ? "btn-warning" : "btn-success"}
                  >
                    {tenant.isActive ? "無効化" : "有効化"}
                  </Button>
                  <Button
                    onClick={() => setDeleteConfirm(true)}
                    disabled={actionLoading}
                    variant="destructive"
                    size="sm"
                  >
                    削除
                  </Button>
                </>
              )}
            </div>
          </div>

          {isEditing ? (
            <div className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">テナント名</span>
                </label>
                <input
                  type="text"
                  value={editForm.name || ""}
                  onChange={e =>
                    setEditForm({ ...editForm, name: e.target.value })
                  }
                  className="input input-bordered w-full"
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">説明</span>
                </label>
                <textarea
                  value={editForm.description || ""}
                  onChange={e =>
                    setEditForm({ ...editForm, description: e.target.value })
                  }
                  rows={3}
                  className="textarea textarea-bordered w-full"
                />
              </div>
              <div className="flex gap-4">
                <Button onClick={handleSave} disabled={actionLoading}>
                  {actionLoading ? (
                    <span className="loading loading-spinner loading-sm"></span>
                  ) : (
                    "保存"
                  )}
                </Button>
                <Button
                  onClick={() => setIsEditing(false)}
                  disabled={actionLoading}
                  variant="ghost"
                >
                  キャンセル
                </Button>
              </div>
            </div>
          ) : (
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  テナント名
                </dt>
                <dd className="text-gray-900">{tenant.name}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  ステータス
                </dt>
                <dd>
                  <span
                    className={`badge ${
                      tenant.isActive ? "badge-success" : "badge-error"
                    }`}
                  >
                    {tenant.isActive ? "有効" : "無効"}
                  </span>
                </dd>
              </div>
              <div className="md:col-span-2">
                <dt className="text-sm font-medium text-gray-500">説明</dt>
                <dd className="text-gray-900">{tenant.description || "-"}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">作成日</dt>
                <dd className="text-gray-900">
                  {new Date(tenant.createdAt).toLocaleString("ja-JP")}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">更新日</dt>
                <dd className="text-gray-900">
                  {new Date(tenant.updatedAt).toLocaleString("ja-JP")}
                </dd>
              </div>
            </dl>
          )}
        </div>
      </div>

      {/* ライセンス管理 */}
      <div className="card bg-base-100 shadow-md mb-6">
        <div className="card-body">
          <h2 className="card-title text-color-title">ライセンス管理</h2>
          <div className="space-y-3">
            {(Object.keys(LICENSE_KEY) as LicenseKey[]).map(key => (
              <div key={key} className="flex items-center justify-between py-2">
                <span className="text-gray-700">{LICENSE_LABELS[key]}</span>
                <input
                  type="checkbox"
                  className="toggle toggle-primary"
                  checked={tenant.licenses.includes(key)}
                  disabled={actionLoading}
                  onChange={e => handleToggleLicense(key, e.target.checked)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* メンバー一覧 */}
      <div className="card bg-base-100 shadow-md">
        <div className="card-body">
          <h2 className="card-title text-color-title">
            メンバー一覧 ({tenant.members.length}名)
          </h2>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>名前</th>
                  <th>メールアドレス</th>
                  <th>ロール</th>
                </tr>
              </thead>
              <tbody>
                {tenant.members.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center text-gray-500">
                      メンバーがいません
                    </td>
                  </tr>
                ) : (
                  tenant.members.map(member => (
                    <tr key={member.userId} className="hover">
                      <td>
                        <Link
                          to={`/admin/users/${member.userId}`}
                          className="link link-primary"
                        >
                          {member.familyName} {member.givenName}
                        </Link>
                      </td>
                      <td className="text-gray-600">{member.email}</td>
                      <td>
                        <select
                          className="select select-bordered select-sm"
                          value={member.role}
                          disabled={isRoleChanging || actionLoading}
                          onChange={e => {
                            const newRole = e.target.value;
                            if (newRole === member.role) return;
                            setRoleChangeConfirm({
                              userId: member.userId,
                              name: `${member.familyName} ${member.givenName}`,
                              newRole,
                            });
                          }}
                        >
                          <option value="owner">owner</option>
                          <option value="admin">admin</option>
                          <option value="member">member</option>
                        </select>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 契約履歴 */}
      <div className="card bg-base-100 shadow-md mb-6">
        <div className="card-body">
          <h2 className="card-title text-color-title">契約履歴</h2>
          {contractsLoading ? (
            <div className="flex justify-center py-4">
              <span className="loading loading-spinner loading-md"></span>
            </div>
          ) : contractsData && contractsData.items.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>プラン</th>
                    <th>サイクル</th>
                    <th>ステータス</th>
                    <th>開始日</th>
                    <th>終了日</th>
                    <th>作成日</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {contractsData.items.map(contract => (
                    <tr key={contract.id}>
                      <td>{contract.planName}</td>
                      <td>{contract.billingCycle}</td>
                      <td>
                        <span
                          className={`badge badge-sm ${
                            contract.status === "active"
                              ? "badge-success"
                              : contract.status === "trial"
                                ? "badge-info"
                                : contract.status === "cancelled"
                                  ? "badge-error"
                                  : "badge-ghost"
                          }`}
                        >
                          {contract.status}
                        </span>
                      </td>
                      <td>{contract.startDate}</td>
                      <td>{contract.endDate}</td>
                      <td>
                        {new Date(contract.createdAt).toLocaleDateString(
                          "ja-JP",
                        )}
                      </td>
                      <td>
                        {(contract.status === "active" ||
                          contract.status === "trial") && (
                          <button
                            className="btn btn-error btn-xs"
                            disabled={isCancelling}
                            onClick={() =>
                              setContractCancelConfirm(contract.id)
                            }
                          >
                            キャンセル
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">契約がありません</p>
          )}
        </div>
      </div>

      {/* 契約キャンセル確認モーダル */}
      {contractCancelConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg text-color-title">
              契約をキャンセルしますか？
            </h3>
            <p className="py-4 text-gray-600">
              この操作により契約が無効化されます。
            </p>
            <div className="modal-action">
              <button
                onClick={() => setContractCancelConfirm(null)}
                disabled={isCancelling}
                className="btn btn-ghost"
              >
                戻る
              </button>
              <button
                onClick={async () => {
                  try {
                    await toastPromise(
                      cancelContract(id!, contractCancelConfirm),
                      {
                        success: "契約をキャンセルしました",
                        error: "契約のキャンセルに失敗しました",
                      },
                    );
                    await refetchContracts();
                  } catch (_e) {
                    // toastPromise がエラー表示済み
                  } finally {
                    setContractCancelConfirm(null);
                  }
                }}
                disabled={isCancelling}
                className="btn btn-error"
              >
                {isCancelling ? (
                  <span className="loading loading-spinner loading-sm"></span>
                ) : (
                  "キャンセルする"
                )}
              </button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !isCancelling && setContractCancelConfirm(null)}
          ></div>
        </div>
      )}

      {/* 削除確認モーダル */}
      {deleteConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg text-color-title">
              テナントを削除しますか？
            </h3>
            <p className="py-4 text-gray-600">
              この操作は取り消すことができません。テナントに関連する全てのデータが削除されます。
            </p>
            <div className="modal-action">
              <Button
                onClick={() => setDeleteConfirm(false)}
                disabled={actionLoading}
                variant="ghost"
              >
                キャンセル
              </Button>
              <Button
                onClick={handleDelete}
                disabled={actionLoading}
                variant="destructive"
              >
                {actionLoading ? (
                  <span className="loading loading-spinner loading-sm"></span>
                ) : (
                  "削除する"
                )}
              </Button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => setDeleteConfirm(false)}
          ></div>
        </div>
      )}

      {/* ロール変更確認モーダル */}
      {roleChangeConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg text-color-title">
              ロールを変更しますか？
            </h3>
            <p className="py-4 text-gray-600">
              {roleChangeConfirm.name} のロールを「{roleChangeConfirm.newRole}
              」に変更します。
            </p>
            <div className="modal-action">
              <button
                onClick={() => setRoleChangeConfirm(null)}
                disabled={isRoleChanging}
                className="btn btn-ghost"
              >
                キャンセル
              </button>
              <button
                onClick={async () => {
                  const { userId, name, newRole } = roleChangeConfirm;
                  try {
                    await toastPromise(changeMemberRole(id!, userId, newRole), {
                      success: `${name} のロールを ${newRole} に変更しました`,
                      error: "ロール変更に失敗しました",
                    });
                    await refetch();
                  } catch (_e) {
                    // toastPromise がエラー表示済み
                  } finally {
                    setRoleChangeConfirm(null);
                  }
                }}
                disabled={isRoleChanging}
                className="btn btn-primary"
              >
                {isRoleChanging ? (
                  <span className="loading loading-spinner loading-sm"></span>
                ) : (
                  "変更する"
                )}
              </button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !isRoleChanging && setRoleChangeConfirm(null)}
          ></div>
        </div>
      )}
    </div>
  );
};

export default TenantDetailPage;
